import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Download, Wrench, Scale, Trash2, FileSignature, RotateCcw, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import ZavorreDialog from "@/components/ZavorreDialog";
import FissaggiTettoDialog from "@/components/FissaggiTettoDialog";

const itDateTime = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(d).slice(0, 10); }
};

const TIPO = {
  zavorre: { label: "Zavorre (tetto piano)", Icon: Scale, cls: "border-amber-300 bg-amber-100 text-amber-800" },
  fissaggi_piano: { label: "Fissaggi tetto piano", Icon: Wrench, cls: "border-blue-300 bg-blue-100 text-blue-800" },
  fissaggi_inclinato: { label: "Fissaggi tetto inclinato", Icon: Wrench, cls: "border-indigo-300 bg-indigo-100 text-indigo-800" },
};

const FILTERS = [
  { k: "", label: "Tutte" },
  { k: "zavorre", label: "Zavorre" },
  { k: "fissaggi_piano", label: "Fissaggi piano" },
  { k: "fissaggi_inclinato", label: "Fissaggi inclinato" },
];

// estrae le falde con matrice dal payload (matrice reale o ricostruita dai conteggi 'rows')
const extractFalde = (p) => {
  const df = Array.isArray(p?.disposizione_falde) ? p.disposizione_falde : [];
  return df.map((f) => {
    if (Array.isArray(f?.matrice) && f.matrice.length) return { id: f.falda_id, matrice: f.matrice };
    if (Array.isArray(f?.rows) && f.rows.length) {
      const w = Math.max(...f.rows, 1);
      return { id: f.falda_id, matrice: f.rows.map((n) => Array.from({ length: w }, (_, i) => (i < n ? 1 : 0))) };
    }
    return null;
  }).filter(Boolean);
};

// griglia visiva del layout pannelli (rispecchiata dx/sx per coerenza con la vista satellitare)
function MatriceMini({ matrice, faldaId }) {
  const grid = matrice.map((r) => [...r].reverse());
  const cols = Math.max(...grid.map((r) => r.length), 1);
  const cell = cols > 24 ? 6 : cols > 16 ? 8 : 10;
  const nPan = grid.reduce((a, r) => a + r.filter(Boolean).length, 0);
  return (
    <div data-testid={`ct-matrice-${faldaId || "campo"}`}>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Layout campo{faldaId && faldaId !== "campo" ? ` · ${faldaId}` : ""} · {nPan} pann.</div>
      <div className="inline-flex flex-col gap-[2px] rounded-md border border-border bg-muted/30 p-1.5">
        {grid.map((r, ri) => (
          <div key={ri} className="flex gap-[2px]">
            {r.map((v, ci) => (<div key={ci} style={{ width: cell, height: cell }} className={v ? "rounded-[2px] bg-primary" : "rounded-[2px] bg-border/40"} />))}
          </div>
        ))}
      </div>
    </div>
  );
}

const riepilogo = (c) => {
  const s = c.summary || {};
  if (c.tipo === "zavorre") {
    const p = s.peso_totale_kg != null ? `${Math.round(s.peso_totale_kg)} kg zavorra` : null;
    return [s.n_prsg != null ? `${s.n_prsg} PRSG` : null, s.n_pannelli != null ? `${s.n_pannelli} pann.` : null, p].filter(Boolean).join(" · ") || "—";
  }
  const t = s.totali || {};
  return [s.n_pannelli != null ? `${s.n_pannelli} pann.` : null,
    t.barre_intere != null ? `${t.barre_intere} barre` : null,
    t.staffe != null ? `${t.staffe} staffe` : null].filter(Boolean).join(" · ") || "—";
};

export default function ConsulenzeTecniche() {
  const [rows, setRows] = useState(null);
  const [tipo, setTipo] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [zavOpen, setZavOpen] = useState(false);
  const [fissOpen, setFissOpen] = useState(false);
  const [zavPreload, setZavPreload] = useState(null);
  const [fissPreload, setFissPreload] = useState(null);
  const [preInst, setPreInst] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [details, setDetails] = useState({});
  const [expLoading, setExpLoading] = useState({});

  const toggle = async (c) => {
    const open = !!expanded[c.id];
    setExpanded((e) => ({ ...e, [c.id]: !open }));
    if (!open && !details[c.id]) {
      setExpLoading((l) => ({ ...l, [c.id]: true }));
      try {
        const r = await api.get(`/consulenze-tecniche/${c.id}`);
        const p = r.data?.payload || {};
        setDetails((d) => ({ ...d, [c.id]: { kw: p.meta?.potenza_kwp ?? c.potenza_kwp ?? null, comune: p.meta?.comune || null, indirizzo: p.meta?.indirizzo || null, falde: extractFalde(p) } }));
      } catch (e) { toast.error("Impossibile caricare il dettaglio"); }
      finally { setExpLoading((l) => ({ ...l, [c.id]: false })); }
    }
  };

  const load = useCallback(() => {
    setRows(null);
    api.get(`/consulenze-tecniche${tipo ? `?tipo=${tipo}` : ""}`)
      .then((r) => setRows(r.data?.consulenze || []))
      .catch(() => setRows([]));
  }, [tipo]);
  useEffect(load, [load]);

  const downloadPdf = async (c) => {
    try {
      const r = await api.get(`/consulenze-tecniche/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = c.pdf_filename || "consulenza-tecnica.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Impossibile scaricare il PDF"); }
  };

  const reopen = async (c) => {
    try {
      const r = await api.get(`/consulenze-tecniche/${c.id}`);
      const rec = r.data || {};
      const payload = rec.payload || {};
      setPreInst({ id: rec.installer_id, name: rec.installer_name, is_partner: rec.is_partner });
      if (rec.tipo === "zavorre") {
        setFissOpen(false); setZavPreload({ payload, key: rec.id }); setZavOpen(true);
      } else {
        setZavOpen(false); setFissPreload({ payload, key: rec.id }); setFissOpen(true);
      }
    } catch (e) { toast.error("Impossibile riaprire la configurazione"); }
  };

  const remove = async (c) => {
    if (!window.confirm("Eliminare definitivamente questa consulenza tecnica (configurazione + PDF)?")) return;
    setDeleting(c.id);
    try {
      await api.delete(`/consulenze-tecniche/${c.id}`);
      toast.success("Consulenza eliminata");
      load();
    } catch (e) { toast.error("Impossibile eliminare"); }
    finally { setDeleting(null); }
  };

  return (
    <div className="space-y-6" data-testid="consulenze-tecniche-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight">Consulenze tecniche</h1>
        <p className="text-sm text-muted-foreground">Configurazioni e PDF archiviati di Fissaggi (tetto inclinato/piano) e calcolo Zavorre. Salvati manualmente dagli strumenti in Tools.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Button key={f.k} size="sm" variant={tipo === f.k ? "default" : "outline"} onClick={() => setTipo(f.k)} data-testid={`ct-filter-${f.k || "all"}`}>{f.label}</Button>
        ))}
      </div>
      <Card className="p-4">
        {rows === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Cliente finale</TableHead>
                  <TableHead>Installatore</TableHead>
                  <TableHead>Riepilogo</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-12 text-center text-muted-foreground">Nessuna consulenza tecnica archiviata.</TableCell></TableRow>}
                {rows.map((c) => {
                  const t = TIPO[c.tipo] || { label: c.tipo, Icon: Wrench, cls: "" };
                  const Icon = t.Icon;
                  const open = !!expanded[c.id];
                  const det = details[c.id];
                  return [
                    (
                    <TableRow key={c.id} data-testid={`ct-row-${c.id}`}>
                      <TableCell className="text-sm text-muted-foreground">
                        <button onClick={() => toggle(c)} data-testid={`ct-expand-${c.id}`} title="Mostra dettaglio" className="mr-1.5 inline-flex align-middle text-muted-foreground transition-colors hover:text-primary">
                          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                        {itDateTime(c.created_at)}
                      </TableCell>
                      <TableCell><Badge className={`gap-1 ${t.cls}`}><Icon className="h-3 w-3" /> {t.label}</Badge></TableCell>
                      <TableCell className="text-sm font-semibold">{c.cliente || "—"}{c.is_partner ? <Badge variant="secondary" className="ml-2 text-[10px]">Partner</Badge> : null}</TableCell>
                      <TableCell className="text-sm">{c.installer_name || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{riepilogo(c)}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center justify-end gap-2">
                          <button onClick={() => reopen(c)} title="Riapri configurazione" data-testid={`ct-reopen-${c.id}`} className="inline-flex items-center text-muted-foreground transition-colors hover:text-primary"><RotateCcw className="h-4 w-4" /></button>
                          <button onClick={() => downloadPdf(c)} title="Scarica PDF" data-testid={`ct-pdf-${c.id}`} className="inline-flex items-center text-muted-foreground transition-colors hover:text-primary"><Download className="h-4 w-4" /></button>
                          <button onClick={() => remove(c)} disabled={deleting === c.id} title="Elimina" data-testid={`ct-delete-${c.id}`} className="inline-flex items-center text-muted-foreground transition-colors hover:text-destructive disabled:opacity-40">
                            {deleting === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                    ),
                    open && (
                    <TableRow key={`${c.id}-det`} data-testid={`ct-detail-${c.id}`} className="bg-muted/20 hover:bg-muted/20">
                      <TableCell colSpan={6} className="py-4">
                        {expLoading[c.id] || !det ? (
                          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
                        ) : (
                          <div className="flex flex-wrap items-start gap-8">
                            <div className="space-y-2">
                              <div><div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Potenza</div><div className="text-lg font-bold" data-testid={`ct-kw-${c.id}`}>{det.kw != null ? `${det.kw} kWp` : "—"}</div></div>
                              {det.comune && <div><div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Comune</div><div className="text-sm">{det.comune}</div></div>}
                              {det.indirizzo && <div className="max-w-[220px]"><div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Indirizzo</div><div className="text-sm">{det.indirizzo}</div></div>}
                            </div>
                            <div className="flex flex-wrap gap-4">
                              {det.falde.length ? det.falde.map((f, i) => <MatriceMini key={i} matrice={f.matrice} faldaId={f.id} />) : <span className="text-sm text-muted-foreground">Layout non disponibile</span>}
                            </div>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                    ),
                  ];
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <ZavorreDialog open={zavOpen} onOpenChange={(v) => { setZavOpen(v); if (!v) { setZavPreload(null); load(); } }}
        installerId={preInst?.id} installerName={preInst?.name} installerIsPartner={preInst?.is_partner}
        preload={zavPreload?.payload} preloadKey={zavPreload?.key} />
      <FissaggiTettoDialog open={fissOpen} onOpenChange={(v) => { setFissOpen(v); if (!v) { setFissPreload(null); load(); } }}
        installerId={preInst?.id} installerName={preInst?.name} installerIsPartner={preInst?.is_partner}
        pdfMode preload={fissPreload?.payload} preloadKey={fissPreload?.key} onApply={() => {}} />
    </div>
  );
}
